import React, { useState } from 'react';
import { Upload } from 'lucide-react';
import { withBackend } from '../utils/api';

const QAComparison = () => {
  const [file1, setFile1] = useState(null);
  const [file2, setFile2] = useState(null);
  const [version1, setVersion1] = useState('Version 1');
  const [version2, setVersion2] = useState('Version 2');
  const [qaType, setQaType] = useState('both'); // 'content', 'design', or 'both'
  const [leniency, setLeniency] = useState('balanced'); // 'strict', 'balanced', 'lenient'
  const [isComparing, setIsComparing] = useState(false);
  const [comparisonData, setComparisonData] = useState([]);
  const [hasCompared, setHasCompared] = useState(false);

  const handleFile1Upload = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      setFile1(file);
      setVersion1(file.name.replace(/\.[^/.]+$/, ''));
    }
  };

  const handleFile2Upload = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      setFile2(file);
      setVersion2(file.name.replace(/\.[^/.]+$/, ''));
    }
  };

  const handleStartComparison = async () => {
    if (!file1 || !file2) {
      alert('Please upload both files before comparing');
      return;
    }

    setIsComparing(true);
    setHasCompared(false);

    try {
      let allDifferences = [];

      // Run content QA if needed
      if (qaType === 'content' || qaType === 'both') {
        const formData = new FormData();
        formData.append('file1', file1);
        formData.append('file2', file2);

        const response = await fetch(withBackend('/qa-content-comparison'), {
          method: 'POST',
          body: formData
        });

        if (!response.ok) {
          throw new Error(`Content QA failed: ${response.status}`);
        }

        const result = await response.json();
        allDifferences = [...allDifferences, ...result.differences.map(d => ({ ...d, qaCategory: 'Content QA' }))];
      }

      // Run design QA if needed
      if (qaType === 'design' || qaType === 'both') {
        const formData = new FormData();
        formData.append('file1', file1);
        formData.append('file2', file2);
        formData.append('leniency', leniency);

        const response = await fetch(withBackend('/qa-design-comparison'), {
          method: 'POST',
          body: formData
        });

        if (!response.ok) {
          throw new Error(`Design QA failed: ${response.status}`);
        }

        const result = await response.json();
        allDifferences = [...allDifferences, ...result.differences.map(d => ({ ...d, qaCategory: 'Design QA' }))];
      }

      // Transform backend data to frontend format
      const transformedData = allDifferences.map((diff, idx) => ({
        id: idx + 1,
        changeType: diff.type || 'Unknown',
        description: diff.description,
        slide: diff.slide,
        version1: diff.before || diff.details?.[0] || '-',
        version2: diff.after || diff.details?.[1] || '-',
        severity: diff.severity || 'medium',
        qaCategory: diff.qaCategory || 'Unknown',
        typeColor: getSeverityColor(diff.severity)
      }));

      setComparisonData(transformedData);
      setHasCompared(true);
    } catch (error) {
      console.error('Comparison error:', error);
      alert(`Failed to compare files: ${error.message}`);
    } finally {
      setIsComparing(false);
    }
  };

  const getSeverityColor = (severity) => {
    switch (severity) {
      case 'high':
        return 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300';
      case 'medium':
        return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300';
      case 'low':
        return 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300';
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-300';
    }
  };

  return (
    <div className="relative flex h-auto min-h-screen w-full flex-col font-display bg-background text-white">
      <div className="flex h-full grow flex-col">
        <div className="flex flex-1 flex-col lg:flex-row">
          {/* Sidebar */}
          <aside className="w-full lg:w-96 flex-shrink-0 bg-background lg:border-r border-surface p-6">
            <div className="flex flex-col gap-6">
              <h2 className="text-2xl font-bold text-white">QA Comparison</h2>

              {/* File Upload */}
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-muted mb-2">Version 1 (Original)</label>
                  <label className="flex flex-col items-center justify-center w-full h-32 border-2 border-dashed rounded-lg cursor-pointer bg-surface hover:bg-surface/80 border-border hover:border-primary transition-colors">
                    <div className="flex flex-col items-center justify-center pt-5 pb-6">
                      <Upload className="w-8 h-8 mb-2 text-muted" />
                      <p className="text-xs text-muted">
                        {file1 ? file1.name : 'Click to upload PPTX'}
                      </p>
                    </div>
                    <input
                      type="file"
                      className="hidden"
                      accept=".pptx,.ppt"
                      onChange={handleFile1Upload}
                    />
                  </label>
                </div>

                <div>
                  <label className="block text-sm font-medium text-muted mb-2">Version 2 (Comparison)</label>
                  <label className="flex flex-col items-center justify-center w-full h-32 border-2 border-dashed rounded-lg cursor-pointer bg-surface hover:bg-surface/80 border-border hover:border-primary transition-colors">
                    <div className="flex flex-col items-center justify-center pt-5 pb-6">
                      <Upload className="w-8 h-8 mb-2 text-muted" />
                      <p className="text-xs text-muted">
                        {file2 ? file2.name : 'Click to upload PPTX'}
                      </p>
                    </div>
                    <input
                      type="file"
                      className="hidden"
                      accept=".pptx,.ppt"
                      onChange={handleFile2Upload}
                    />
                  </label>
                </div>
              </div>

              {/* QA Type Selection */}
              <div className="space-y-3">
                <h3 className="text-lg font-semibold text-white">QA Type</h3>
                <div className="space-y-2">
                  <label className="flex items-center cursor-pointer">
                    <input
                      className="sr-only peer"
                      name="qaType"
                      type="radio"
                      checked={qaType === 'both'}
                      onChange={() => setQaType('both')}
                    />
                    <div className="w-full rounded-lg px-4 py-3 text-sm font-medium text-center text-muted bg-surface border border-surface peer-checked:bg-primary peer-checked:text-background peer-checked:border-primary transition-all duration-200">
                      Both (Content + Design)
                    </div>
                  </label>
                  <label className="flex items-center cursor-pointer">
                    <input
                      className="sr-only peer"
                      name="qaType"
                      type="radio"
                      checked={qaType === 'content'}
                      onChange={() => setQaType('content')}
                    />
                    <div className="w-full rounded-lg px-4 py-3 text-sm font-medium text-center text-muted bg-surface border border-surface peer-checked:bg-primary peer-checked:text-background peer-checked:border-primary transition-all duration-200">
                      Content QA Only
                    </div>
                  </label>
                  <label className="flex items-center cursor-pointer">
                    <input
                      className="sr-only peer"
                      name="qaType"
                      type="radio"
                      checked={qaType === 'design'}
                      onChange={() => setQaType('design')}
                    />
                    <div className="w-full rounded-lg px-4 py-3 text-sm font-medium text-center text-muted bg-surface border border-surface peer-checked:bg-primary peer-checked:text-background peer-checked:border-primary transition-all duration-200">
                      Design QA Only
                    </div>
                  </label>
                </div>
                <p className="text-xs text-muted">
                  {qaType === 'both' && 'Checks both text content and brand guideline compliance'}
                  {qaType === 'content' && 'Ensures text content is identical between versions'}
                  {qaType === 'design' && 'Checks brand guideline compliance (colors, fonts, layout)'}
                </p>
              </div>

              {/* Leniency Level (only for Design QA) */}
              {(qaType === 'design' || qaType === 'both') && (
                <div className="space-y-3">
                  <h3 className="text-lg font-semibold text-white">Leniency Level</h3>
                  <div className="space-y-2">
                    {['strict', 'balanced', 'lenient'].map((level) => (
                      <label key={level} className="flex items-center cursor-pointer">
                        <input
                          className="sr-only peer"
                          name="leniency"
                          type="radio"
                          checked={leniency === level}
                          onChange={() => setLeniency(level)}
                        />
                        <div className="w-full rounded-lg px-4 py-3 text-sm font-medium text-center text-muted bg-surface border border-surface peer-checked:bg-primary peer-checked:text-background peer-checked:border-primary transition-all duration-200 capitalize">
                          {level}
                        </div>
                      </label>
                    ))}
                  </div>
                  <p className="text-xs text-muted">
                    {leniency === 'strict' && 'No deviations from brand guidelines allowed'}
                    {leniency === 'balanced' && 'Minor deviations acceptable'}
                    {leniency === 'lenient' && 'Flexible with brand guideline variations'}
                  </p>
                </div>
              )}

              {/* Start Comparison Button */}
              <div className="mt-4">
                <button
                  onClick={handleStartComparison}
                  disabled={isComparing || !file1 || !file2}
                  className="w-full rounded-lg bg-primary py-3 text-base font-bold text-background shadow-lg shadow-primary/30 transition-transform duration-200 hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isComparing ? (
                    <div className="flex items-center justify-center gap-2">
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-background"></div>
                      Analyzing...
                    </div>
                  ) : !file1 || !file2 ? (
                    'Upload both files to compare'
                  ) : (
                    'Start Comparison'
                  )}
                </button>
              </div>

              {/* Statistics */}
              <div className="bg-surface rounded-xl p-6 border border-surface">
                <h3 className="text-lg font-semibold text-white mb-4">Summary</h3>
                <div className="space-y-3">
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-muted">Total Changes:</span>
                    <span className="text-sm font-semibold text-white">{comparisonData.length}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-muted">High Severity:</span>
                    <span className="text-sm font-semibold text-red-400">{comparisonData.filter(d => d.severity === 'high').length}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-muted">Medium Severity:</span>
                    <span className="text-sm font-semibold text-yellow-400">{comparisonData.filter(d => d.severity === 'medium').length}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-muted">Low Severity:</span>
                    <span className="text-sm font-semibold text-blue-400">{comparisonData.filter(d => d.severity === 'low').length}</span>
                  </div>
                </div>
              </div>
            </div>
          </aside>

          {/* Main Content */}
          <main className="flex-1 p-6 lg:p-8">
            <div className="flex flex-col gap-8">
              <div>
                <h1 className="text-3xl font-bold text-white">QA Comparison Results</h1>
                <p className="text-sm text-muted">
                  Comparing "{version1}" with "{version2}"
                </p>
              </div>

              {!hasCompared ? (
                <div className="flex flex-col items-center justify-center py-16 text-center bg-surface rounded-lg border border-surface">
                  <Upload className="h-16 w-16 text-muted mb-4" />
                  <h3 className="text-xl font-semibold text-white mb-2">Upload Files to Compare</h3>
                  <p className="text-muted">Upload two PowerPoint files and click "Start Comparison" to see differences</p>
                </div>
              ) : comparisonData.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-16 text-center bg-surface rounded-lg border border-surface">
                  <svg className="h-16 w-16 text-muted mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <h3 className="text-xl font-semibold text-white mb-2">No Changes Found</h3>
                  <p className="text-muted">The files are identical - no differences detected</p>
                </div>
              ) : (
                <div className="overflow-x-auto rounded-lg border border-surface bg-surface">
                  <table className="min-w-full divide-y divide-surface">
                    <thead className="bg-surface">
                      <tr>
                        <th className="px-6 py-4 text-left text-xs font-bold uppercase tracking-wider text-muted" scope="col">
                          Category
                        </th>
                        <th className="px-6 py-4 text-left text-xs font-bold uppercase tracking-wider text-muted" scope="col">
                          Severity
                        </th>
                        <th className="px-6 py-4 text-left text-xs font-bold uppercase tracking-wider text-muted" scope="col">
                          Type
                        </th>
                        <th className="px-6 py-4 text-left text-xs font-bold uppercase tracking-wider text-muted" scope="col">
                          Slide
                        </th>
                        <th className="px-6 py-4 text-left text-xs font-bold uppercase tracking-wider text-muted" scope="col">
                          Description
                        </th>
                        <th className="px-6 py-4 text-left text-xs font-bold uppercase tracking-wider text-muted" scope="col">
                          Details
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-surface">
                      {comparisonData.map((row) => (
                        <tr key={row.id} className="hover:bg-background/50 transition-colors">
                          <td className="whitespace-nowrap px-6 py-4">
                            <span className={`inline-flex items-center rounded-md px-2.5 py-0.5 text-xs font-medium ${row.qaCategory === 'Content QA' ? 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300' : 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300'}`}>
                              {row.qaCategory}
                            </span>
                          </td>
                          <td className="whitespace-nowrap px-6 py-4">
                            <span className={`inline-flex items-center rounded-md px-2.5 py-0.5 text-sm font-medium ${row.typeColor}`}>
                              {row.severity}
                            </span>
                          </td>
                          <td className="px-6 py-4 text-sm text-white whitespace-nowrap">
                            {row.changeType.replace(/_/g, ' ')}
                          </td>
                          <td className="px-6 py-4 text-sm text-white">
                            {row.slide ? `Slide ${row.slide}` : '-'}
                          </td>
                          <td className="px-6 py-4 text-sm text-white">
                            {row.description}
                          </td>
                          <td className="px-6 py-4 text-sm">
                            {row.version1 !== '-' && row.version2 !== '-' ? (
                              <div className="space-y-1">
                                <div className="text-accent line-through">{row.version1}</div>
                                <div className="text-primary font-semibold">{row.version2}</div>
                              </div>
                            ) : (
                              <span className="text-muted">-</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </main>
        </div>
      </div>
    </div>
  );
};

export default QAComparison;
